import { action } from '@ember/object';
import RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  type FormListSelectionChangedArgs,
  FormListSelectionOption,
} from 'donor-elf-web/components/form/list-selection';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import { serializeAppliedFilters } from 'donor-elf-web/lib/filter-utils-new';
import ContactModel from 'donor-elf-web/models/contact';
import AbilitiesService from 'donor-elf-web/services/abilities';
import ContactsCache from 'donor-elf-web/services/contacts-cache';

interface ComponentArgs {
  contact: ContactModel;
}

export default class ContactDetailsGroupsComponent extends Component<ComponentArgs> {
  @service declare abilities: AbilitiesService;
  @service declare contactsCache: ContactsCache;
  @service declare router: RouterService;

  @tracked editing = false;
  @tracked saving = false;
  @tracked saveError?: any;

  @tracked listOptions?: FormListSelectionOption[];
  selectedListOptions: FormListSelectionOption[] = [];

  get showEditIcon() {
    return (
      this.abilities.hasAbility('edit_contact') && !this.editing && !this.saving
    );
  }

  @action
  setEditing(val: boolean) {
    this.editing = val;
    if (this.editing) {
      this.populateListOptions();
    } else {
      this.listOptions = undefined;
      this.selectedListOptions = [];
    }
  }

  populateListOptions() {
    let selectedGroups = this.args.contact.groups || [];
    this.listOptions = sortByProperties(
      this.contactsCache.sortedGroups.map((x, index) => {
        return new FormListSelectionOption({
          val: x,
          selected: selectedGroups.includes(x),
          index,
        });
      }),
      ['selected:desc', 'index:asc'],
    );
    this.selectedListOptions = this.listOptions.filter((x) => x.selected);
  }

  @action
  viewGroup(groupName: string) {
    this.router.transitionTo('funds.fund.contacts.index', {
      queryParams: {
        filters: serializeAppliedFilters([
          { propertyName: 'contactGroups', operation: 'any', val: [groupName] },
        ]),
      },
    });
  }

  @action
  handleListSelectionsChanged(args: FormListSelectionChangedArgs) {
    this.selectedListOptions = args.selectedListOptions;
  }

  @action
  async save() {
    this.editing = false;
    this.saving = true;
    this.saveError = undefined;
    let contact = this.args.contact;
    let previousGroups = contact.groups;
    contact.groups = this.selectedListOptions.map((x) => x.val);
    try {
      await contact.save();
    } catch (error) {
      this.saveError = error;
      contact.groups = previousGroups;
    }
    this.saving = false;
  }

  @action
  handleCancel() {
    this.editing = false;
    this.saving = false;
    this.saveError = undefined;
  }
}
